import { semanticColors } from "./colors";
import { typography } from "./typography";

// Shared styling for the outlined fields used across the app's forms and table
// filters (payments, charges, transfers, commissions, video consultations), so
// they look the same everywhere and the values live in one place.
//
// The rule: labels and placeholders are prompts and read grey
// (`text.secondary`); what the user actually typed reads dark (`text.primary`).

const fieldRoot = {
  borderRadius: "8px",
  background: semanticColors.surface.canvas,
  fontFamily: typography.body.l.fontFamily,
  "& fieldset": { borderColor: semanticColors.text.primary },
  "&:hover fieldset": { borderColor: semanticColors.text.primary },
  "&.Mui-focused fieldset": { borderColor: semanticColors.border.focus }
};

/** Label (floating) and placeholder — the prompt, never the filled-in value. */
export const fieldPromptSx = {
  ...typography.body.l,
  color: semanticColors.text.secondary
};

/** The value the user typed/selected. */
export const fieldValueSx = {
  ...typography.body.l,
  color: semanticColors.text.primary
};

const fieldLabelAndInput = {
  "& .MuiInputLabel-root": fieldPromptSx,
  "& .MuiInputLabel-root.Mui-focused": { color: semanticColors.border.focus },
  "& .MuiInputBase-input": {
    ...fieldValueSx,
    "&::placeholder": { ...fieldPromptSx, opacity: 1 }
  },
  "& .MuiSelect-select": fieldValueSx
};

/** Standard 52px-tall outlined field. */
export const outlinedFieldSx = {
  "& .MuiOutlinedInput-root": { height: "52px", ...fieldRoot },
  ...fieldLabelAndInput
};

/** Same styling but auto-height, for multiline fields (e.g. notes). */
export const outlinedFieldMultilineSx = {
  "& .MuiOutlinedInput-root": fieldRoot,
  ...fieldLabelAndInput
};

/**
 * Softens the outline of a field in error from red to the amber used by the
 * "Expirada" appointment status: a past time or a too-short duration is
 * something to correct before scheduling, not a failure. Only the outline
 * changes — the label stays grey like any other prompt, and the helper text
 * underneath stays red so the reason still reads as an error.
 */
export const warningOutlineSx = {
  "& .MuiOutlinedInput-root.Mui-error fieldset, & .MuiOutlinedInput-root.Mui-error:hover fieldset, & .MuiOutlinedInput-root.Mui-error.Mui-focused fieldset": {
    borderColor: semanticColors.status.inProgress
  },
  // MUI turns the floating label red in the error state; keep it a plain prompt.
  "& .MuiInputLabel-root.Mui-error": fieldPromptSx
};

/**
 * The column a form lives in.
 *
 * `ContainerWrapper` is sized for the dashboards and tables (up to 1800px),
 * which leaves a form stretched edge to edge on a desktop screen. Forms read
 * better in a narrow measure, so the fields sit in a capped, centred column —
 * the same shape the personal/professional data screens already use.
 */
export const formColumnSx = {
  display: "flex",
  flexDirection: "column",
  gap: 3,
  width: "100%",
  maxWidth: "760px",
  mx: "auto"
};

/** Leading search icon shared by the patient/search filters. */
export const searchIconSx = {
  color: semanticColors.text.secondary,
  fontSize: 18
};
